import { Component, Injector, Input, OnInit } from "@angular/core";
import { BaseComponent } from "ng/src/app/shared/base.component";
import { Report } from 'ng/src/app/reports/report.model';
import { ReportService } from "ng/src/app/reports/report.service";
import { catchError, map, switchMap, takeUntil } from "rxjs/operators";
import { AlertService } from "ng/src/app/alerts/shared/alert.service";
import { Observable, throwError } from "rxjs";
import { ConfirmationService } from "primeng/api";
import { ReportAlertTrigger } from "ng/src/app/alerts/shared/alert.model";
import { Optional } from 'ng/src/app/shared/model/app.model';
import { Log } from 'ng/src/app/logs/log.model';
import { Template } from 'ng/src/app/templates/shared/template.model';
import { AlertTriggerEditorComponent } from "ng/src/app/templates/trigger-editor/alert-trigger-editor.component";
import { RollbackValueAlias } from "ng/src/app/core/value";

@Component({
	selector: 'lb-alert-triggers[log][template]',
	template: `
		<lb-loading-indicator *ngIf="loading" label="Loading Alert Triggers"></lb-loading-indicator>
		<ng-container *ngIf="editing && log && template">
			<div class="p-grid p-mt-1">
				<div class="p-col-6 p-fluid">
					<div class="p-inputgroup">
						<span class="p-inputgroup-addon">Name</span>
						<input type="text" [placeholder]="autofillNewReportName" [(ngModel)]="reportNameRef!.value" pInputText>
					</div>
				</div>
				<div class="p-col-6 p-text-right">
		            <button pButton (click)="dismissTriggerEditor(false, triggerEditor)" class="p-button-secondary p-button-rounded p-mr-1" label="Cancel" icon="pi pi-times"></button>
                    <button pButton (click)="dismissTriggerEditor(true, triggerEditor)" class="p-button-success p-button-rounded" label="Save" icon="pi pi-check"></button>
				</div>
			</div>
			<lb-trigger-editor #triggerEditor [template]="template" [report]="editing" (onDismiss)="editing = undefined"></lb-trigger-editor>
		</ng-container>
		<ng-container *ngIf="!editing">
            <div class="p-grid">
                <div class="p-col-12 p-text-right">
                    <button pButton (click)="startEditingNewReport()" class="p-button-success p-button-rounded" label="Create" icon="pi pi-plus"></button>
                </div>
            </div>
            <p-table *ngIf="!loading && !editing" [value]="reports" dataKey="id" styleClass="p-datatable-striped">
                <ng-template pTemplate="body" let-report>
                    <tr>
                        <td>
                            {{report.name}}
                        </td>
                        <td style="width: 20%; text-align: right">
                            <p-confirmPopup [key]="getConfirmKey(report)"></p-confirmPopup>
                            <button pButton title="Edit" icon="pi pi-pencil" (click)="editing = report"></button>
                            <button pButton [pRowToggler]="report" title="View Versions" class="p-ml-1 p-button-secondary" icon="pi pi-bars"></button>
                            <button pButton (click)="deleteTriggerReport(report, $event);" title="Delete" class="p-ml-1 p-button-danger" icon="pi pi-trash"></button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="rowexpansion" let-report>
                    <tr>
                        <td colspan="2">
                            <lb-report-version-manager (onEdit)="editing = $event" [baseObject]="report"></lb-report-version-manager>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="2">
                            <div class="p-text-center">
                                <label>No Alert Triggers Found</label>
                            </div>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
		</ng-container>
	`
})
export class AlertTriggersComponent extends BaseComponent implements OnInit {
	@Input() log!: Log;
	@Input() template!: Template;
	
	reports = new Array<Report>();
	loading = false;
	
	creatingNewReport = false;
	showingNewReportDialog = false;
	newReportName = '';
	
	private _editing?: Report;
	reportNameRef?: RollbackValueAlias<string>;
	
	get editing() {
		return this._editing;
	}
	
	set editing(val) {
		this._editing = val;
		if (val) {
			this.reportNameRef = new RollbackValueAlias<string>(() => val.name, name => val.name = name);
			this.reportNameRef.saveState();
		}
		else
			delete this.reportNameRef;
	}
	
	get autofillNewReportName() {
		return `${this.template.name} Trigger ${this.formatDate(new Date())}`;
	}
	
	constructor(
		private reportService: ReportService,
		private alertService: AlertService,
		private confirmService: ConfirmationService,
		injector: Injector
	) {
		super(injector);
	}
	
	ngOnInit(): void {
		this.updateTemplateTriggerReports();
	}
	
	updateTemplateTriggerReports() {
		this.loading = false;
		return this.reportService.fetch({
			sort: 'name,asc',
			log: this.log.id,
			template: this.template.id,
			templateTrigger: true,
			activeRevision: true
		})
			.pipe(
				takeUntil(this.onDestroy$),
				switchMap(res => {
					return this.getTemplateRelatedTrigger().pipe(
						map(trigger => res.filter(r => r.id !== trigger?.reportId))
					);
				})
			)
			.subscribe(reports => {
				this.reports = reports;
			})
			.add(() => this.loading = false);
	}
	
	deleteTriggerReport(report: Report, event: Event, confirmed = false) {
		if (!confirmed) {
			this.confirmService.confirm({
				key: this.getConfirmKey(report),
				target: event.target ?? undefined,
				header: `Delete Trigger`,
				message: `Deleting ${report.name} will remove the published version, revisions and drafts. Would you like to delete ${report.name}?`,
				accept: () => this.deleteTriggerReport(report, event, true),
				acceptButtonStyleClass: 'p-button-danger',
				rejectButtonStyleClass: 'p-button-secondary',
				defaultFocus: 'reject'
			})
			return;
		}
		
		const idx = this.reports.indexOf(report);
		this.reports.splice(idx, 1);
		this.reportService.deleteReport(report)
			.pipe(
				catchError(err => {
					this.reports.splice(idx, 0, report);
					return throwError(err);
				})
			)
			.subscribe();
	}
	
	getConfirmKey(report: Report) {
		return `report-trigger-${report.id}`;
	}
	
	startEditingNewReport() {
		this.editing = {
			name: ''
		} as any;
		this.showingNewReportDialog = false;
	}
	
	dismissTriggerEditor(save: boolean, editor: AlertTriggerEditorComponent) {
		if (save && !this.editing!.name) {
			this.editing!.name = this.autofillNewReportName;
		}
		return editor.dismiss(save)
			.then(() => {
				delete this.editing;
				return this.updateTemplateTriggerReports();
			})
	}
	
	private getTemplateRelatedTrigger(): Observable<Optional<ReportAlertTrigger>> {
		return this.alertService.fetchTriggers({
			'alertObject.forTemplate': this.template.id,
			'report.activeRevision': true
		})
			.pipe(
				takeUntil(this.onDestroy$),
				map(res => res[0])
			)
	}
}